import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Tag, Loader2, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const EUR = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);

export default function Checkout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [promoCode, setPromoCode] = useState(sessionStorage.getItem("novatus_promo_code") || "");
  const [promoApplied, setPromoApplied] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data?.plans || []));
  }, []);

  // Auto-apply promo if user came from register flow
  useEffect(() => {
    if (promoCode && !promoApplied) verifyPromo();
  }, []);  // eslint-disable-line

  const myPlan = plans.find((p) => p.role === user?.role);

  const verifyPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    try {
      const r = await api.post("/billing/redeem-promo", { code });
      setPromoApplied(r.data);
      toast.success(`Promo-Code "${r.data.code}" erkannt`, { description: r.data.label });
    } catch (e) {
      setPromoApplied(null);
      toast.error(e.response?.data?.detail || "Promo-Code ungültig");
    }
  };

  const removePromo = () => {
    setPromoCode("");
    setPromoApplied(null);
    sessionStorage.removeItem("novatus_promo_code");
  };

  const initiateCheckout = async () => {
    setLoading(true);
    try {
      const r = await api.post("/billing/create-session", {
        origin_url: window.location.origin,
        promo_code: promoApplied?.code || undefined,
      });
      if (r.data.already_active) {
        toast.info("Du hast bereits ein aktives Abo.");
        navigate("/dashboard");
        return;
      }
      if (r.data.url) {
        window.location.href = r.data.url;
        return;
      }
      toast.error("Checkout-URL fehlt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout konnte nicht gestartet werden");
    } finally {
      setLoading(false);
    }
  };

  // Compute display amount
  const monthly = myPlan?.monthly_eur || 0;
  const setupFee = promoApplied?.setup_fee_eur || 0;
  const bonusMonths = promoApplied?.bonus_months || 0;
  const paidMonths = Math.max(0, 6 - bonusMonths);
  const total = setupFee + paidMonths * monthly;

  if (!myPlan) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <div className="max-w-md bg-[#18181B] border border-zinc-800 rounded-2xl p-6 text-center">
          <AlertCircle className="mx-auto mb-3 text-amber-400" size={36} />
          <h1 className="font-display text-xl font-bold mb-2">Kein Plan verfügbar</h1>
          <p className="text-sm text-zinc-300 font-medium">Für deine Rolle ({user?.role}) gibt es kein Abo. Bitte Admin kontaktieren.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col items-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-lg">
        <p className="font-mono text-[12px] text-[#00E575] tracking-[0.3em] uppercase mb-2 text-center">Schritt 3 von 3</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-center tracking-tight mb-2">
          Abo aktivieren
        </h1>
        <p className="text-center text-zinc-400 text-sm mb-8">
          6-Monats-Initialabo, danach monatliche Verlängerung.
        </p>

        {/* Plan card */}
        <div className="bg-[#18181B] border-2 border-[#00E575]/40 rounded-2xl p-5 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="font-mono text-[12px] text-[#00E575] tracking-widest uppercase">Dein Plan</div>
              <h2 className="font-display text-2xl font-bold leading-tight">{myPlan.label}-Abo</h2>
            </div>
            <span className="px-2 py-1 rounded-md bg-[#00E575]/10 border border-[#00E575]/40 text-[#00E575] text-[12px] font-bold uppercase tracking-widest">
              {EUR(monthly)} / Mon.
            </span>
          </div>

          {/* Cost breakdown */}
          <div className="border-t border-zinc-800 pt-3 mt-3 space-y-1.5 text-[13px]">
            <div className="flex justify-between text-zinc-300">
              <span>{paidMonths} × Monatsrate</span>
              <span className="font-mono">{EUR(paidMonths * monthly)}</span>
            </div>
            {bonusMonths > 0 && (
              <div className="flex justify-between text-[#00E575]">
                <span>+ {bonusMonths} Bonus-Monat{bonusMonths > 1 ? "e" : ""} (kostenlos)</span>
                <span className="font-mono">0,00 €</span>
              </div>
            )}
            {setupFee > 0 && (
              <div className="flex justify-between text-amber-300">
                <span>+ Setup-Gebühr ({promoApplied?.code})</span>
                <span className="font-mono">{EUR(setupFee)}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-zinc-800 pt-2 mt-2 text-zinc-100 font-bold text-base">
              <span>Heute zu zahlen</span>
              <span className="font-mono text-[#00E575]">{EUR(total)}</span>
            </div>
            <p className="text-[12px] text-zinc-300 italic pt-1">
              Einmalige Vorauszahlung für die 6-Monats-Initiallaufzeit.
            </p>
          </div>
        </div>

        {/* Promo input */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 mb-6">
          <label className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest text-zinc-300 mb-2">
            <Tag size={13} /> Promo-Code
          </label>
          {promoApplied ? (
            <div data-testid="promo-applied-card" className="flex items-start gap-3 bg-[#00E575]/5 border border-[#00E575]/30 rounded-xl p-3">
              <CheckCircle2 className="text-[#00E575] shrink-0 mt-0.5" size={18} />
              <div className="flex-1">
                <div className="font-bold text-[#00E575]">{promoApplied.code}</div>
                <div className="text-[12px] text-zinc-300 mt-0.5">{promoApplied.label}</div>
              </div>
              <button type="button" onClick={removePromo} className="text-[12px] text-zinc-400 hover:text-zinc-100 underline">
                Entfernen
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                data-testid="checkout-promo-input"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="z.B. ULI4711"
                className="flex-1 h-11 bg-[#09090B] border border-zinc-700 rounded-xl px-3 text-zinc-50 text-sm uppercase tracking-wider focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
              />
              <button
                type="button"
                data-testid="checkout-promo-apply"
                onClick={verifyPromo}
                disabled={!promoCode.trim()}
                className="px-4 h-11 rounded-xl bg-zinc-800 border border-zinc-700 hover:border-[#00E575] hover:text-[#00E575] text-zinc-200 text-sm font-medium transition disabled:opacity-50"
              >
                Prüfen
              </button>
            </div>
          )}
        </div>

        {/* Checkout button */}
        <button
          type="button"
          data-testid="checkout-pay-btn"
          onClick={initiateCheckout}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#00E575] to-[#00C964] text-[#09090B] font-bold text-base h-14 rounded-2xl active:scale-[0.98] transition glow-green hover:shadow-[0_0_30px_rgba(0,229,117,0.4)] disabled:opacity-60"
        >
          {loading ? (
            <Loader2 className="animate-spin" size={20} />
          ) : (
            <>
              <Sparkles size={18} /> Jetzt {EUR(total)} per Stripe zahlen
            </>
          )}
        </button>
        <p className="text-center text-[12px] text-zinc-300 mt-3">
          Sichere Zahlung via Stripe · Test-Modus aktiv
        </p>
      </div>
    </div>
  );
}
